import type { ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { OrderProps, WidthProps } from 'styled-system';
import { order, space, width } from 'styled-system';

import type { SpaceProps } from '../../theme/system-types';
import { Flex } from '../Flex';
import SelectCard from '../SelectCard';
import RadioIcon from './RadioIcon';
import RadioInput from './RadioInput';
import type { RadioPosition } from './types';

interface RadioCardProps {
  label: string;
  labelId?: string;
  name: string;
  checked: boolean;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  position?: RadioPosition;
  footer?: ReactNode;
  className?: string;
  children?: ReactNode | ReactNode[];
  ref?: React.Ref<HTMLDivElement>;
}

const Icon = styled(RadioIcon)<OrderProps>`
  ${order};
`;

const Head = styled.div<WidthProps & SpaceProps>`
  ${width};
  ${space};
`;

const Footer = styled.div<SpaceProps>`
  ${space};
`;

const getHeadStyles = (isIconRight: boolean): SpaceProps => ({
  [isIconRight ? 'mr' : 'ml']: 'sp_12',
});

const getFooterStyles = (isIconRight: boolean): SpaceProps => ({
  ml: isIconRight ? 'sp_12' : 'sp_32',
  pl: isIconRight ? 'sp_0' : 'sp_4',
});

const HiddenRadioInput = styled(RadioInput)`
  opacity: 0;
  position: absolute;
  width: 1px;
  height: 1px;
`;

const RadioCard = ({
  children,
  position,
  footer,
  checked,
  className,
  onChange,
  disabled = false,
  ref,
  ...props
}: RadioCardProps) => {
  const { label, labelId, value, name, ...restProps } = props;
  const isIconRight = position === 'right';
  const headStyles = getHeadStyles(isIconRight);
  const footerStyles = getFooterStyles(isIconRight);
  const handleSelect = () => {
    if (!disabled) {
      onChange(value);
    }
  };

  return (
    <SelectCard
      ref={ref}
      disabled={disabled}
      selected={checked}
      onClick={handleSelect}
      className={className}
      data-testid={`radio-card-${value}`}
      {...restProps}
    >
      <Flex alignItems="center">
        <HiddenRadioInput
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={handleSelect}
          {...(labelId ? { 'aria-labelledby': labelId } : { 'aria-label': label })}
        />
        <Icon
          checked={checked}
          fill={disabled ? 'sys.neutral.icon.default' : 'none'}
          order={isIconRight ? 1 : 0}
        />
        <Head width={1} {...headStyles}>
          {children}
        </Head>
      </Flex>
      {footer && <Footer {...footerStyles}>{footer}</Footer>}
    </SelectCard>
  );
};

RadioCard.displayName = 'RadioCard';

export default RadioCard;
